/**
 * Content of the help page (`pages/HelpPage.tsx`), modelled on the customer portal's help.
 *
 * Static on purpose: it describes how the backoffice works, not tenant data, and an endpoint
 * that served a constant would be a round trip for a document.
 *
 * Writing rules. The screens carry a "?" only where a label does not say enough (D43,
 * `HELP_TIPS`); everything longer stands here:
 * - Short. One question, a few sentences; a restated label is not an answer.
 * - Button labels verbatim in »…«, exactly as the screen prints them, so a reader can find
 *   them with the browser's search.
 * - An entry arrives with the button it names. No entry for a screen that does not exist yet.
 * - No internal system names and no commands: the lint guard in `eslint.config.js` rejects
 *   them in every string of this app, this file included.
 * - German with real umlauts, no dashes (comma, colon or parentheses instead).
 */

import type { BackofficeElement, SponsorRight } from "@/lib/labels";

/** Where a step happens: the screen (`where`, linked when `path` is set) and its control. */
export interface HelpAction {
  readonly label: string;
  readonly where: string;
  readonly path?: string;
}

export interface HelpEntry {
  /** Anchor of the entry, so a link can point at `/hilfe#<id>`. */
  readonly id: string;
  readonly question: string;
  /** Paragraphs. */
  readonly answer: readonly string[];
  readonly figure?: "booking-states" | "field-states";
  readonly actions?: readonly HelpAction[];
}

export interface HelpSection {
  readonly id: string;
  readonly title: string;
  readonly entries: readonly HelpEntry[];
}

export const HELP_UPDATED_AT = "22.10.2026";

/**
 * The text behind each "?" of the backoffice (D43): a few sentences rather than a paragraph, only
 * at fields whose label does not say what they do. Fourteen since the IT lead asked on 19.10.2026
 * for the deadline alerts, the live dashboard, the prize upgrade, outbound links and the two
 * switches of the sponsors' maintenance; seventeen with the notifications, self-publishing and the
 * door window's presentation (22.10.2026). Another needs a reason.
 */
export const HELP_TIPS = {
  submissionLead:
    "So viele Tage vor dem Öffnen des Türchens muss der Sponsor seinen Beitrag eingereicht haben. Leer: keine Einreichfrist und keine Erinnerungen.",
  approvalLead:
    "So viele Tage vor dem Öffnen muss der Beitrag freigegeben sein, sonst öffnet das Türchen mit dem Ersatzinhalt. Leer: das gilt erst am Tag der Öffnung.",
  minimumBooking:
    "Stichtag, bis zu dem genug Türchen belegt sein sollen. Er wird nur gespeichert, nichts wird dadurch gesperrt.",
  fallback:
    "Text und Bild für jedes Türchen, das an seinem Tag keinen freigegebenen Beitrag hat. Der Aufbau ist der Standard-Aufbau dieser Seite.",
  consentCoupling:
    "Als Pflicht ist diese Einwilligung Voraussetzung der Teilnahme. Die Teilnahmebedingungen müssen das nennen.",
  embedOrigins:
    "Eine Adresse je Zeile, nur Schema und Host, z. B. https://www.physiotherapeuten.de. Leer: keine Website darf den Kalender einbetten.",
  doorPresentation:
    "Eigene Seite: Das Türchen öffnet als ganze Seite. Fenster über dem Kalender: Es öffnet mittig über dem abgedunkelten Kalender, Bilder dann nur 16:9 oder 4:3. Auf Websites ohne Einbettungsskript öffnet es immer als Seite.",
  sponsorLayout:
    "Gilt für jedes Türchen, außer das Türchen oder sein Sponsor legt etwas anderes fest. Ob Seite oder Dialog, bestimmen weiter Sie.",
  ownLayout: "Ohne Häkchen folgt der Aufbau der Aktion (Gestaltung › Türchen-Fenster).",
  testAction:
    "Teilnahmen an einer Testaktion kommen nicht in die Ziehung und erzeugen keine Leads. Nur im Entwurf änderbar.",
  deadlineAlerts:
    "Diese E-Mails erfahren, wenn ein Sponsor trotz Erinnerung nicht einreicht oder ein Türchen zur Freigabefrist noch nicht freigegeben ist.",
  leads:
    "Teilnehmer können an den Türchen des Sponsors der Weitergabe ihrer Kontaktdaten zustimmen, der Sponsor erhält sie nach Annahme der Übermittlungsvereinbarung. Ohne Häkchen fragt kein Türchen danach.",
  stats:
    "Der Sponsor sieht die Zahlen seiner Türchen im Portal unter »Auswertung«, jederzeit. Ohne Häkchen nie.",
  prizeUpgrade:
    "Der Sponsor trägt den Gewinn ein und darf ihn nach dem Einreichen bis zum Ende seines Türchens noch aufwerten (nachbessern). Das wird protokolliert und braucht keine neue Freigabe.",
  linkButtons:
    "Achtung: Link-Knöpfe und Links im Text führen Teilnehmer aus dem Kalender auf fremde Websites.",
  sponsorEdits:
    "Sponsoren pflegen Texte, Bilder, Quiz und Gewinn ihres Türchens im Portal selbst und reichen sie ein, Sie geben frei. Ohne Häkchen pflegen Sie die Inhalte, und der Sponsor gibt frei.",
  selfPublish:
    "Ein vollständiges Türchen, dessen Dateien geprüft sind, steht mit dem Veröffentlichen des Sponsors ohne Ihre Freigabe im Kalender. Änderungen bis zum Tag des Türchens veröffentlicht der Sponsor ebenso selbst. Nehmen Sie die Freigabe zurück, steht das Türchen mit dem nächsten Veröffentlichen wieder im Kalender; heraus hält es dann nur der Not-Aus.",
  sponsorPictures:
    "Sponsoren laden im Portal ihr Logo und die Bilder in die Bildplätze ihrer Türchen. Die Texte bleiben bei Ihnen.",
  notifications:
    "Die Glocke oben rechts sieht, wer den Bereich bearbeiten darf, etwa »Sponsor reicht ein« alle, die Türchen pflegen. E-Mails an das Backoffice gehen an »Fristwarnungen an«. Einladungen und Widerrufe gehen immer per E-Mail.",
} as const;

/**
 * The "?" texts that name the sponsor, as a customer's calendar and the tab »Kunden« say them
 * (E8). The same sentences with »Kunde« and »Kalenderzugang«.
 */
export const CUSTOMER_HELP_TIPS = {
  leads:
    "Teilnehmer können der Weitergabe ihrer Kontaktdaten an den Kunden zustimmen, der Kunde erhält sie nach Annahme der Übermittlungsvereinbarung. Ohne Häkchen fragt kein Türchen danach.",
  stats:
    "Der Kunde sieht die Zahlen seiner Türchen im Kalenderzugang unter »Auswertung«, jederzeit. Ohne Häkchen nie.",
  // Without »Fristwarnungen an«: a customer's calendar does not show the field.
  notifications:
    "Die Glocke oben rechts sieht, wer den Bereich bearbeiten darf, etwa »Kunde gibt frei« alle, die Türchen freigeben. Einladungen und Widerrufe gehen immer per E-Mail.",
  // Without the approval way: in their own calendar the customer publishes themselves (E5).
  sponsorEdits:
    "Der Kunde pflegt Texte, Bilder, Quiz und Gewinn seiner Türchen im Kalenderzugang selbst und veröffentlicht sie ohne Ihre Freigabe. Ohne Häkchen pflegen Sie die Inhalte.",
  sponsorPictures:
    "Der Kunde lädt im Kalenderzugang sein Logo und die Bilder in die Bildplätze seiner Türchen. Die Texte bleiben bei Ihnen.",
  prizeUpgrade:
    "Der Kunde trägt den Gewinn ein und darf ihn bis zum Ende seines Türchens noch aufwerten (nachbessern). Das wird protokolliert.",
} as const satisfies Partial<Record<keyof typeof HELP_TIPS, string>>;

/** The "?" beside the sponsor rights whose label alone does not say what they allow. */
export const SPONSOR_RIGHT_TIPS: Readonly<Partial<Record<SponsorRight, string>>> = {
  link_buttons: HELP_TIPS.linkButtons,
  prize: HELP_TIPS.prizeUpgrade,
};

/**
 * The "?" beside the rows of the grants page whose label alone does not say what the element
 * opens (IT lead, 21.10.2026). An element without an entry gets no mark.
 */
export const ELEMENT_TIPS: Readonly<Partial<Record<BackofficeElement, string>>> = {
  customer_action:
    "Eine Kopie einer Vorlage, deren Türchen alle ein Kunde belegt und die ab Anlage den Inhalt der Vorlage zeigt.",
  action_organizer:
    "Name, Anschrift und Impressum-Link des Veranstalters. Die Einwilligungstexte nennen ihn.",
  action_embed: "Welche Websites den Kalender einbetten dürfen und wie er dort aussieht.",
  action_identity: "Bei welchem Dienst sich Teilnehmer anmelden. Nur im Entwurf änderbar.",
  sponsor_workflow:
    "Ob Sponsoren die Inhalte ihrer Türchen selbst pflegen oder nur freigeben und was sie im Portal ändern dürfen, etwa wie viele Folien. Gilt für alle Sponsoren der Aktion.",
  calendar_layout: "Mosaik, Wabe oder Raster, Spalten je Bildschirmgröße und Kachelform.",
  calendar_style:
    "Tür, Türform, Hervorhebung des heutigen und Markierung der vergangenen Türchen, Farbe der Nacht.",
  door_number_style:
    "Schrift, Farbe, Größe und Position der Zahl auf den Türchen, nicht ihre Anzahl.",
  door_window:
    "Ob ein Türchen als Seite oder Dialog aufgeht und wie Bild, Titel und Text darin stehen.",
  door_design: "Zahl und Lage des Logos für ein einzelnes Türchen, abweichend von der Aktion.",
  door_layout:
    "Aufbau des Fensters (Bild, Titel, Text) für ein einzelnes Türchen, abweichend von der Aktion.",
  lead_fields:
    "Felder, Texte und Einwilligungen des Teilnahmeformulars. Änderbar, bis das erste Türchen öffnet.",
  account_offer: "Ob die Abschlussseite der Teilnahme anbietet, ein Kundenkonto anzulegen.",
  example_view:
    "Eine Musteransicht der Aktion mit Beispielpartnern und eigenem Link, etwa für Screenshots im Vertrieb.",
  action_test_flag: HELP_TIPS.testAction,
  door_placement: "Einen Sponsor auf ein Türchen setzen oder die Belegung wieder lösen.",
  door_invitation:
    "Die Einladung ins Portal an den Sponsor schicken oder den Einladungslink anzeigen.",
  sponsor_master: "Firma, Ansprechpartner, Leads, Auswertung und »Zugang stilllegen«.",
  sponsor_access:
    "Weitere Personen der Firma ins Sponsorenportal einladen und ihre Konten lösen.",
  sponsor_rights:
    "Für einen einzelnen Sponsor von »Pflege durch Sponsoren« abweichen, etwa weniger Folien erlauben.",
  test_data: "Entfernt, was Probeläufe in der Vorschau hinterlassen haben.",
  audit: "Wer wann was geändert oder abgerufen hat.",
  emergency:
    "Kalender schließen, Versand anhalten, ein Türchen vom Netz nehmen, eine Teilnahme sperren.",
  approve_unscanned:
    "Bilder und Logos mit Begründung freigeben, deren Prüfung noch läuft oder fehlgeschlagen ist. Abgelehnte nie.",
};

/** Screens whose help section carries the screen's own path as its anchor. */
const SECTION_PAGES = ["sponsoren", "gewinne", "not-aus", "teilnehmer", "stammdaten"] as const;

/**
 * Where the header's "Hilfe" opens for the screen at `pathname` and `search`: `#<section>`, or
 * nothing for a screen without a section of its own. One link per page, from one place (IT lead, 19.10.2026).
 */
export function helpAnchor(pathname: string, search = ""): string {
  if (/^\/aktionen\/[^/]+\/tuerchen\//.test(pathname)) {
    return "#freigabe";
  }
  if (
    /^\/aktionen\/[^/]+\/?$/.test(pathname) &&
    new URLSearchParams(search).get("ansicht") === "kalender"
  ) {
    return "#teilnehmersicht";
  }
  if (pathname === "/" || pathname.startsWith("/aktionen/")) {
    return "#aktionen";
  }
  const page = SECTION_PAGES.find((id) => pathname === `/${id}` || pathname.startsWith(`/${id}/`));
  return page === undefined ? "" : `#${page}`;
}

const SPONSOR_FORM = "Sponsoren → Firma";
const DOOR = "Aktionen → Aktion → Türchen";
const HEADER = "Aktionen → Aktion, neben dem Titel";

export const HELP_SECTIONS: readonly HelpSection[] = [
  {
    id: "aktionen",
    title: "Aktionen und Türchenplan",
    entries: [
      {
        id: "tuerchenplan",
        question: "Wo sehe ich, wie die Türchen einer Aktion stehen?",
        answer: [
          "Unter »Aktionen« den Titel der Aktion öffnen. Der Türchenplan zeigt jedes Türchen mit Sponsor, Stand der Belegung (»Offen«, bis eine Einladung an die Firma versendet ist), Inhalt (Folien, Videos, Fragen, Gewinnbild) und Öffnung. »Kennzahlen zeigen« blendet die Zahlen ein: Aufrufe, Antworten (ein wiederholtes Quiz zählt einmal), begonnene Registrierungen, Teilnahmen, Leads.",
          "Die Schaltflächen über der Tabelle filtern nach dem Stand der Belegung. Ein Klick auf die Zeile öffnet das Türchen. »Kalender« zeigt die Teilnehmerseite der Aktion.",
        ],
        actions: [{ label: "Türchenplan", where: "Aktionen → Aktion", path: "/" }],
      },
      {
        id: "hindernisse",
        question: "Was fehlt meiner Aktion noch zum Livegang?",
        answer: [
          "Über den Reitern einer Aktion steht, was noch fehlt. Solange dort etwas steht, bleibt »Live schalten« neben dem Titel gesperrt.",
          "Eine Einwilligung, die die Aktion als Pflicht abfragt, braucht einen freigegebenen Wortlaut, sonst bleibt »Live schalten« gesperrt. Fehlt der Wortlaut einer freiwilligen, steht das oben im Reiter »Einstellungen«: Sie hält den Livegang nicht auf, aber das Häkchen fehlt, beim Sponsorenkontakt also jeder Lead.",
          "Mehrere Aktionen können gleichzeitig live sein, je Marke eine. Jede hat ihre eigene Adresse: der Kalender-Domain folgt der Link der Aktion, etwa /advent-2026-pt/. Adresse und Code zum Einbetten auf einer Website stehen unter »Einstellungen« im Abschnitt »Website«, wie der Kalender dort aussieht, unter »Gestaltung« bei »Auf Websites«.",
        ],
        actions: [{ label: "Live schalten", where: HEADER, path: "/" }],
      },
      {
        id: "nach-livegang",
        question: "Was lässt sich nach dem Livegang nicht mehr ändern?",
        answer: [
          "Zeitraum, Zahl der Türchen und Zeitzone stehen schon beim Anlegen fest. Mit dem Livegang sperren außerdem der Anmeldedienst und die Testmarkierung: Wer sich angemeldet hat, meldet sich beim nächsten Mal am selben Dienst an.",
          "Das Teilnahmeformular (Gestaltung, Ansicht »Formular«: Felder, Texte, Einwilligungen) bleibt änderbar, bis das erste Türchen öffnet. Bis dahin gibt es keine echte Teilnahme und damit keine Einwilligung, die eine Änderung entwerten könnte. Danach nennen die Einwilligungen die Felder, und ändern lassen sie sich nur noch auf dem Notweg über die IT-Leitung.",
          "Titel, Fristen, Veranstalter, Ersatzinhalt und Kundenkonto bleiben änderbar, jede Änderung steht im Protokoll. Teilnahmebedingungen erscheinen als neue Fassung, wer schon zugestimmt hat, behält seine.",
          "Eine beendete Aktion lässt sich nicht mehr ändern.",
        ],
      },
      {
        id: "speichern",
        question: "Wie speichere ich Einstellungen?",
        answer: [
          "Reiter mit Einstellungen haben unten eine Leiste mit einem einzigen »Speichern«. Es übernimmt alle geänderten Abschnitte des Reiters nacheinander, jeder Abschnitt steht einzeln im Protokoll. Fehlt eine Pflichtangabe, hält die Leiste dort an und nennt den Abschnitt.",
          "Wer den Reiter mit ungespeicherten Änderungen verlässt, wird vorher gefragt. Eigene Knöpfe behalten Schritte, die mehr tun als speichern: Live schalten, eine neue Fassung der Teilnahmebedingungen, Hochladen, Einreichen.",
          "Bei einer neuen Aktion schlägt das Backoffice im Formular Vor- und Nachname als Pflichtfelder vor. Bis zum Speichern steht in der Leiste »Vorschlag noch nicht gespeichert«; das nächste »Speichern« übernimmt ihn mit. So steht es auch oben im Reiter »Einstellungen«. »Live schalten« prüft das nicht: vor dem Livegang den Vorschlag mit »Speichern« übernehmen, sonst fragt das Formular nur die E-Mail-Adresse ab. Verlassen lässt sich der Reiter ohne Frage.",
        ],
      },
      {
        id: "rechte",
        question: "Warum fehlt mir ein Abschnitt oder ein Knopf?",
        answer: [
          "Was jemand bedienen darf, legt der Superadmin je Formular, Abschnitt und Knopf unter »Berechtigungen« fest. Was einer Rolle nicht freigegeben ist, erscheint nicht; in der Gestaltung steht es schreibgeschützt da.",
          "»Pflege durch Sponsoren« öffnet zunächst nur der Superadmin. Wo diese Hilfe eine Berechtigung nennt, ist der Startwert gemeint.",
        ],
      },
      {
        id: "testaktion",
        question: "Was ist eine Testaktion?",
        answer: [
          "Teilnahmen an einer Testaktion kommen nicht in die Ziehung und erzeugen keine Leads. So lässt sich der ganze Ablauf ausprobieren, ohne dass Gewinner entstehen.",
          "»Als Testaktion markieren« und »Testmarkierung entfernen« gibt es nur im Entwurf. Eine laufende Testaktion lässt sich jederzeit beenden.",
        ],
        actions: [{ label: "Als Testaktion markieren", where: HEADER, path: "/" }],
      },
      {
        id: "beenden",
        question: "Wann lässt sich eine Aktion beenden?",
        answer: [
          "Eine echte Aktion erst nach dem Ende der Nachlauffrist, bis dahin stehen den Sponsoren Leads zu. Jedes Türchen muss gezogen sein, und keine Gewinnzuteilung darf mehr offen sein. Solange der Kalender geschlossen ist, wird nicht gezogen. Eine Testaktion jederzeit.",
          "»Endgültig beenden« steht neben dem Titel, sobald die Laufzeit vorbei ist, bei einer Testaktion immer. Danach schließen Kalender und Sponsorenportal, Ziehung und Erinnerungen laufen nicht mehr. Bereits eingereihte Mails und Mitteilungen über widerrufene Leads gehen noch raus, der Versand lässt sich im Not-Aus weiter anhalten.",
          "»Archivieren« legt eine beendete Aktion ab, ihre Daten bleiben erhalten. Sie steht dann unter Aktionen › Archiv, dort holt der Superadmin sie in den Einstellungen zurück. Einen Entwurf archiviert nur der Superadmin.",
        ],
        actions: [
          { label: "Endgültig beenden", where: HEADER, path: "/" },
          { label: "Archivieren", where: HEADER, path: "/" },
        ],
      },
      {
        id: "kennzahlen",
        question: "Was zählen die Kennzahlen?",
        answer: [
          "Summen über alle Aktionen. Einzelne Teilnehmerdaten stehen hier nie. Sie werden nur bei konkretem Anlass geöffnet, und jeder Zugriff wird protokolliert.",
          "Registrierte Konten: abgeschlossene Registrierungen. Bestätigte E-Mails: Nur wer seine E-Mail bestätigt hat, kommt in den Lostopf.",
          "Teilnahmen: verbuchte Teilnahmen über alle Türchen. Offene Entwürfe: angefangene Teilnahmen ohne abgeschlossene Anmeldung.",
          "Unterdrückte E-Mails: An sie geht keine Mail mehr, wegen Sperre, Abmeldung oder dauerhafter Unzustellbarkeit.",
        ],
      },
    ],
  },
  {
    id: "sponsoren",
    title: "Sponsoren und Belegung",
    entries: [
      {
        id: "belegungszustaende",
        question: "Welche Zustände hat eine Belegung?",
        answer: [
          "Eine Belegung verbindet ein Türchen mit einem Sponsor, seinem Beitrag, der Frage und dem Gewinn. Ihr Zustand ist intern, Teilnehmer sehen ihn nie.",
        ],
        figure: "booking-states",
      },
      {
        id: "einladung",
        question: "Wann bekommt der Sponsor seine Einladung?",
        answer: [
          "Beim Speichern eines Sponsors geht keine Mail raus. Die Einladung gehört zur Belegung eines Türchens.",
          "Vor dem Livegang geht keine Einladung raus. Danach geht sie automatisch raus, spätestens eine Stunde nach dem Belegen oder nach dem Livegang. »Einladung verschicken« reiht sie sofort ein.",
          "Ohne Kontakt-E-Mail geht keine Einladung raus.",
          "Automatisch eingeladen wird nur, wer selbst pflegt oder Bilder und Logo liefert. Einen Sponsor, der nur freigibt, laden Sie auf seiner Seite mit »Einladen« neben der E-Mail ins Portal ein.",
        ],
        actions: [
          { label: "Einladung verschicken", where: DOOR, path: "/" },
          { label: "Einladen", where: SPONSOR_FORM, path: "/sponsoren" },
        ],
      },
      {
        id: "einladungslink",
        question: "Die Einladung ist nicht angekommen. Was jetzt?",
        answer: [
          "»Einladungslink anzeigen« zeigt den Link aus der Mail, um ihn dem Sponsor auf anderem Weg zu geben.",
          "Der Link öffnet die Belegungen und die übermittelten Kontakte des Sponsors. Deshalb nur direkt an den Sponsor geben, nie in ein Ticket oder einen Chat kopieren. Jeder Abruf steht im Protokoll.",
          "Beide Schaltflächen erscheinen ab dem Livegang, mit der Berechtigung »Sponsoren«.",
        ],
        actions: [{ label: "Einladungslink anzeigen", where: DOOR, path: "/" }],
      },
      {
        id: "loesen",
        question: "Was passiert, wenn ich eine Belegung löse?",
        answer: [
          "Die Belegung wird gelöscht, nicht archiviert. Der Grund ist Pflicht und steht mit Ihrer Kennung im Protokoll; danach ist er das Einzige, was die Entscheidung erklärt.",
          "Das Türchen ist wieder frei. Noch nicht verschickte Einladungen und Erinnerungen dieser Belegung entfallen.",
          "Ein Türchen, das live, abgeschlossen oder vom Netz genommen ist, lässt sich nicht lösen. Dafür gibt es den Not-Aus.",
        ],
        actions: [{ label: "Belegung lösen", where: DOOR, path: "/" }],
      },
      {
        id: "stilllegen",
        question: "Was bewirkt »Zugang stilllegen«?",
        answer: [
          "Jeder Einladungslink dieses Sponsors ist ab dem nächsten Klick ungültig, auch ein weitergeleiteter.",
          "»Zugang wieder freigeben« macht das rückgängig.",
        ],
        actions: [{ label: "Zugang stilllegen", where: SPONSOR_FORM, path: "/sponsoren" }],
      },
      {
        id: "leere-eingabe",
        question: "Ich habe ein Eingabefeld im Sponsorformular geleert. Ist der Wert gelöscht?",
        answer: [
          "Nein. Ein leeres Eingabefeld lässt den gespeicherten Wert unverändert.",
          "Nur die Häkchen »Leads« und »Auswertung« werden immer gespeichert, gesetzt wie ungesetzt.",
        ],
        actions: [{ label: "Speichern", where: SPONSOR_FORM, path: "/sponsoren" }],
      },
      {
        id: "leads",
        question: "Was bewirken »Leads« und »Auswertung«?",
        answer: [
          "Ohne »Leads« fragt kein Türchen des Sponsors nach der Weitergabe der Kontaktdaten, es entstehen keine Leads, und der Sponsor sieht keine Übermittlungsvereinbarung. »Liste im Sponsorenportal« und »CSV-Export« legen fest, wie er seine Leads abruft.",
          "Mit »Auswertung« sieht der Sponsor die Zahlen seiner Türchen im Portal, jederzeit. Ohne Häkchen nie, auch nicht nach Ende der Aktion.",
          "Neue Sponsoren starten ohne beides.",
        ],
        actions: [{ label: "Leads", where: SPONSOR_FORM, path: "/sponsoren" }],
      },
      {
        id: "kontaktdaten",
        question: "Warum zeigt die Sponsorenliste keine Kontaktdaten?",
        answer: [
          "Absichtlich. Die Kontaktdaten laden erst auf der Seite eines Sponsors. So stehen sie nicht für alle Sponsoren zugleich auf dem Bildschirm.",
          "Was die Liste zeigt, wählt »Spalten«; der Browser merkt sich die Wahl. »Eingeladen« heißt: an jemanden der Firma ist eine Einladung versendet. »Aktionen« nennt die laufenden Aktionen mit einem Türchen des Sponsors, als Kürzel der Marke oder als Anfang des Titels.",
        ],
        actions: [{ label: "Bearbeiten", where: "Sponsoren", path: "/sponsoren" }],
      },
    ],
  },
  {
    id: "freigabe",
    title: "Beitrag und Freigabe",
    entries: [
      {
        id: "zu-pruefen",
        question: "Wo finde ich die Beiträge, die auf eine Prüfung warten?",
        answer: [
          "Im Türchenplan auf »Zu prüfen« klicken. Die Liste zeigt dann nur eingereichte Beiträge und solche in Prüfung. Ein Klick auf die Nummer öffnet das Türchen mit dem Beitrag.",
        ],
        actions: [{ label: "Zu prüfen", where: "Aktionen → Aktion", path: "/" }],
      },
      {
        id: "ablauf",
        question: "Wie läuft eine Freigabe?",
        answer: [
          "Reicht der Sponsor ein: »Prüfung beginnen«, dann in der Vorschau neben dem Türchen die Seite ansehen, die Teilnehmer am Anzeigetag sehen. Danach »Freigeben« oder »Zurückgeben«.",
          "Der Kommentar beim Zurückgeben geht an den Sponsor. Seine Einreichfrist läuft weiter.",
          "Mit »Sponsor nicht benachrichtigen« geht keine Mail hinaus, etwa wenn Sie ohnehin mit dem Sponsor telefonieren. Den Kommentar sieht er trotzdem in seinem Formular.",
        ],
        actions: [
          { label: "Prüfung beginnen", where: DOOR, path: "/" },
          { label: "Freigeben", where: DOOR, path: "/" },
          { label: "Zurückgeben", where: DOOR, path: "/" },
        ],
      },
      {
        id: "wer-gestaltet",
        question: "Wer pflegt ein Türchen, der Sponsor oder Sie?",
        answer: [
          "Das entscheidet »Sponsor pflegt selbst« in den Einstellungen der Aktion, Abschnitt »Pflege durch Sponsoren«. Auf der Seite eines Sponsors gilt mit »Für diesen Sponsor abweichend« etwas anderes nur für ihn.",
          "Mit Häkchen pflegt der Sponsor Texte, Bilder, Quiz und Gewinn im Portal und reicht ein, Sie geben frei. Was er dort ändern darf, steht unter »Sponsoren dürfen«.",
          "Ohne Häkchen pflegen Sie die Inhalte, der Sponsor sieht an und gibt frei oder wünscht Änderungen. Mit »Bilder und Logo liefern« lädt er Logo und Bilder selbst in die Bildplätze, die Texte bleiben bei Ihnen.",
        ],
        actions: [
          { label: "Sponsor pflegt selbst", where: "Aktionen → Aktion → Einstellungen", path: "/" },
        ],
      },
      {
        id: "nach-dem-einreichen",
        question: "Wer ändert einen eingereichten Beitrag?",
        answer: [
          "Der Sponsor nicht mehr: Mit dem Einreichen steht er für diesen Stand ein. Bearbeiten kann er ihn erst wieder, wenn Sie ihn mit Kommentar zurückgeben.",
          "Sie ändern ihn in jedem Zustand: im Türchen auf »Türchen bearbeiten«. Die Änderung wartet als Korrekturfassung, bis Sie sie mit »An den Sponsor zur Freigabe senden« schicken und er sie freigibt oder bis Sie »Direkt veröffentlichen …« wählen; bis dahin sehen Teilnehmer den bisherigen Stand. Das Protokoll hält den Stand vorher und nachher fest.",
          "Solange eine Korrektur wartet, heißt »Freigeben« »Eingereichte Fassung freigeben«: Es gibt die Einreichung frei, die Korrektur bleibt stehen.",
        ],
        actions: [{ label: "Bearbeiten", where: DOOR, path: "/" }],
      },
      {
        id: "an-den-sponsor",
        question: "Wie gibt der Sponsor Ihre Fassung frei?",
        answer: [
          "Jede Ihrer Änderungen am Türchen eines Sponsors braucht seine Freigabe, auch wenn er nie eingereicht hat. Speichern gibt nur eigene Türchen frei.",
          "Ist das Türchen fertig: »An den Sponsor zur Freigabe senden«. Das ist Ihre Freigabe. Der Sponsor sieht die Fassung im Portal und gibt frei oder schreibt einen Änderungswunsch. Senden geht ab dem Livegang, wenn das Türchen vollständig ist und alle Dateien geprüft sind.",
          "Mit »Sponsor nicht benachrichtigen« geht keine Mail hinaus, die Anfrage steht trotzdem im Portal. Von selbst erinnert nichts, »Erinnerung senden« schickt eine Mail.",
          "Speichern zieht eine offene Anfrage zurück, danach neu senden. »Korrektur verwerfen« löscht die Korrekturfassung, das Türchen behält die bisherige. »Anfrage zurückziehen« nimmt nur die Anfrage zurück. Der Türchenplan zeigt »Beim Sponsor«, »Freigabe überfällig« und »Änderungswunsch«.",
        ],
        actions: [
          { label: "An den Sponsor zur Freigabe senden", where: DOOR, path: "/" },
          { label: "Erinnerung senden", where: DOOR, path: "/" },
        ],
      },
      {
        id: "direkt-veroeffentlichen",
        question: "Der Sponsor gibt nicht frei. Kann ich ohne ihn veröffentlichen?",
        answer: [
          "Ja, mit »Direkt veröffentlichen …« und einer Begründung. Das gibt Ihre Fassung frei, auch eine Korrekturfassung. Die Begründung steht mit Ihrer Kennung im Protokoll.",
          "Bedingungen: vollständig, alle Dateien geprüft; anders als Senden auch vor dem Livegang. Senden, »Erinnerung senden« und »Direkt veröffentlichen …« brauchen die Berechtigung »Freigabe«.",
        ],
        actions: [{ label: "Direkt veröffentlichen …", where: DOOR, path: "/" }],
      },
      {
        id: "eigenes-tuerchen",
        question: "Wie fülle ich ein eigenes Türchen?",
        answer: [
          "Das Türchen mit dem Sponsor belegen, der in der Liste als »Veranstalter« markiert ist. Es gibt ihn immer; er braucht keinen Kontakt und kein Portal und bekommt keine Mails. Teilnehmer lesen auf seinen Türchen den Veranstalter der Aktion, die Marke, wenn die Aktion den Veranstalter nicht nennt.",
          "Dann im Türchen »Türchen bearbeiten«, ausfüllen und speichern. Ist das Türchen vollständig und sind alle Dateien geprüft, ist es damit freigegeben; die Checkliste oben im Türchen nennt, was noch fehlt. »Freigabe zurücknehmen« macht es wieder zum Entwurf, solange es nicht geöffnet hat.",
        ],
        actions: [
          { label: "Belegen", where: DOOR, path: "/" },
          { label: "Bearbeiten", where: DOOR, path: "/" },
        ],
      },
      {
        id: "ersatzinhalt",
        question: "Was heißt »Ersatzinhalt«?",
        answer: [
          "Ein Türchen ohne freigegebenen Beitrag öffnet am Anzeigetag mit dem Ersatzinhalt der Aktion. Das gilt für unbelegte Türchen und für Beiträge, die noch nicht freigegeben sind; nach der Freigabe zeigt das Türchen den Beitrag.",
          "Text und Bild des Ersatzinhalts stehen unter »Gestaltung« in der Ansicht »Türchen-Fenster« bei »Unbelegtes Türchen«. Ohne Ersatzinhalt bleibt »Live schalten« gesperrt.",
          "Nach der Freigabefrist gehen keine Einladung und keine Erinnerung mehr von selbst raus. Belegen, von Hand einladen und freigeben können Sie weiterhin; der Türchenplan zeigt »Frist überschritten«, und die E-Mails unter »Fristwarnungen an« bekommen eine Nachricht (leer: die Antwort-E-Mail der Marke). Am Zustand des Türchens ändert die Frist nichts.",
        ],
      },
      {
        id: "bild-ungeprueft",
        question: "Das Bild ist nicht geprüft. Kann ich trotzdem freigeben?",
        answer: [
          "»Freigeben« bleibt gesperrt, bis die Prüfung das Bild als unbedenklich meldet.",
          "Läuft die Prüfung noch oder ist sie fehlgeschlagen, gibt frei, wer die Berechtigung »Störfall« hat: mit »Ungeprüft freigeben« und einer Begründung. Die Begründung steht mit seiner Kennung im Protokoll.",
          "Ein Bild, das die Prüfung abgelehnt hat, gibt niemand frei. Den Beitrag zurückgeben, damit der Sponsor ein anderes Bild hochlädt.",
          "Ein ungeprüftes Logo des Sponsors hält »Freigeben« nicht auf, erscheint dann aber nicht. »Ungeprüft freigeben« veröffentlicht es mit, ein abgelehntes Logo nie.",
        ],
        actions: [
          { label: "Ungeprüft freigeben", where: DOOR, path: "/" },
          { label: "Zurückgeben", where: DOOR, path: "/" },
        ],
      },
    ],
  },
  {
    id: "teilnehmersicht",
    title: "Was Teilnehmer sehen",
    entries: [
      {
        id: "tuerchenzustaende",
        question: "Welche Zustände hat ein Türchen für Teilnehmer?",
        answer: [
          "Der Zustand ergibt sich aus dem Kalender, der eigenen Teilnahme und dem Not-Aus. Das Beispiel zeigt alle Zustände zu einem festen Zeitpunkt, so wie der Kalender sie zeichnet.",
        ],
        figure: "field-states",
      },
      {
        id: "vorschau",
        question: "Was zeigt die Vorschau?",
        answer: [
          "Die echte Teilnehmerseite der Aktion, schon ab dem Entwurf. Jedes Türchen ist geöffnet, so wie es an seinem Tag aussieht, auch mit Beiträgen, die noch nicht freigegeben sind.",
          "In der Vorschau der laufenden Aktion lässt sich jedes Türchen als Probelauf durchspielen: Antwort, Ergebnis, Anmeldung mit einer eigenen E-Mail-Adresse, Bestätigungsmail und Klick. Ein Probelauf zählt nicht für Verlosung, Statistik, Leads und Tagesmail. Die Vorschau einer Aktion im Entwurf und der Vorschau-Link eines Sponsors nehmen keine Antwort an.",
          "Die Vorschau gilt eine Stunde. Zeigt sie danach einen Hinweis, öffnet »Vorschau neu laden« sie wieder. Ein halb ausgefülltes Formular bleibt bis dahin stehen.",
          "Was die Probeläufe hinterlassen, entfernt, wer die Berechtigung »Datenschutz« hat, im Türchenplan unter »Kalender« mit »Testdaten entfernen«, spätestens vor dem ersten Türchen. Ein echter Teilnehmer mit derselben E-Mail-Adresse bleibt erhalten.",
          "Ist der Kalender geschlossen, bleiben alle Türchen auch in der Vorschau zu. Ein Türchen, das vom Netz genommen ist, bleibt ebenfalls zu.",
          "Auf der Seite eines Türchens steht seine Vorschau immer daneben, zuerst in Handygröße. Nach jedem Speichern lädt sie neu.",
        ],
        actions: [
          { label: "Kalender", where: "Aktionen → Aktion → Türchenplan", path: "/" },
          { label: "Vorschau", where: DOOR, path: "/" },
        ],
      },
    ],
  },
  {
    id: "stammdaten",
    title: "Stammdaten und Texte",
    entries: [
      {
        id: "zeitschriften",
        question: "Woher kommen Name, Farbe und Logo einer Marke?",
        answer: [
          "Aus dem Kundenportal: »Mit dem Kundenportal abgleichen« übernimmt Name, Farbe und Logo. Eine neue Marke erscheint nach dem nächsten Abgleich in der Auswahl von »Aktion anlegen«.",
          "Hier gepflegt werden nur Absender-E-Mail und Antwort-E-Mail. Den Namen, der im Postfach der Teilnehmenden steht, legt jede Aktion unter »Einstellungen« fest.",
        ],
        actions: [
          {
            label: "Mit dem Kundenportal abgleichen",
            where: "Stammdaten",
            path: "/stammdaten",
          },
        ],
      },
      {
        id: "platzhalter",
        question: "Welche Platzhalter darf ein Einwilligungstext enthalten?",
        answer: [
          "Ein Platzhalter steht in geschweiften Klammern und wird beim Anzeigen ersetzt: {action} durch den Titel der Aktion, {controller} durch den Veranstalter, {privacy_policy_url} durch den Link zur Datenschutzerklärung, {fields} durch die Angaben, die übermittelt werden, {sponsor} durch den Namen des Sponsors.",
          "{fields} gibt es nur bei Teilnahme und Sponsorenkontakt, {sponsor} nur beim Sponsorenkontakt. Die erlaubten Platzhalter stehen als Knöpfe unter jedem Textfeld; ein Klick setzt den Platzhalter an die Schreibmarke. Ein Text mit einem anderen Platzhalter wird nicht freigegeben.",
        ],
        actions: [
          {
            label: "Neue Fassung freigeben",
            where: "Stammdaten",
            path: "/stammdaten",
          },
        ],
      },
      {
        id: "neue-fassung",
        question: "Warum überschreibt Speichern den alten Text nicht?",
        answer: [
          "Jede Einwilligung speichert den Wortlaut, dem die Person zugestimmt hat. Deshalb legt »Neue Fassung freigeben« eine neue Fassung an und lässt die alte unverändert. Wer schon zugestimmt hat, behält seinen Wortlaut, neue Teilnehmer sehen die neue Fassung.",
          "Die Freigabe wird mit Ihrer Kennung gespeichert und steht im Protokoll.",
        ],
        actions: [
          {
            label: "Neue Fassung freigeben",
            where: "Stammdaten",
            path: "/stammdaten",
          },
        ],
      },
    ],
  },
  {
    id: "not-aus",
    title: "Not-Aus",
    entries: [
      {
        id: "zwei-schalter",
        question: "Warum gibt es für die Aktion zwei Schalter?",
        answer: [
          "»Kalender schließen« und »Versand anhalten« wirken getrennt. Eine Wartungspause darf den Versand nicht mitnehmen: Sonst bleiben Adressbestätigungen liegen, und wer in dieser Zeit bestätigt hätte, fehlt dauerhaft im Lostopf.",
          "»Kalender öffnen« und »Versand freigeben« heben die Schalter wieder auf.",
        ],
        actions: [
          { label: "Kalender schließen", where: "Not-Aus", path: "/not-aus" },
          { label: "Versand anhalten", where: "Not-Aus", path: "/not-aus" },
        ],
      },
      {
        id: "tuerchen-vom-netz",
        question: "Wie nehme ich ein einzelnes Türchen vom Netz?",
        answer: [
          "Im Türchen unter »Not-Aus« eine Begründung eintragen und »Türchen vom Netz nehmen«. Das überschreibt jeden anderen Zustand des Türchens.",
          "»Türchen wieder freigeben« hebt es auf. Beides braucht die Berechtigung »Störfall«.",
        ],
        actions: [{ label: "Türchen vom Netz nehmen", where: DOOR, path: "/" }],
      },
      {
        id: "teilnahme-sperren",
        question: "Was bewirkt »Teilnahme sperren«?",
        answer: [
          "Ab sofort erhält die Person keine Mail mehr, kann nicht teilnehmen und kommt nicht in den Lostopf. »Sperre aufheben« nimmt das zurück.",
          "Eingegeben wird die E-Mail-Adresse, von der die Beschwerde kam. Abgeglichen wird nur ihre Prüfsumme, sie selbst erscheint in keiner Antwort.",
        ],
        actions: [{ label: "Teilnahme sperren", where: "Not-Aus", path: "/not-aus" }],
      },
    ],
  },
  {
    id: "teilnehmer",
    title: "Teilnehmer",
    entries: [
      {
        id: "sperren-oder-loeschen",
        question: "Teilnahme sperren oder Teilnehmer löschen?",
        answer: [
          "Sperren beendet sofort jede Mail und jede Teilnahme und lässt sich aufheben. Löschen entfernt die Person samt Teilnahmen, Einwilligungen und eingereihten Mails und lässt sich nicht rückgängig machen.",
          "Eine Sperre überdauert das Löschen: Die E-Mail-Adresse bleibt als Prüfsumme auf der Liste der unterdrückten E-Mails.",
          "Übermittelte Leads und Gewinnzuteilungen verhindern das Löschen, weil ihr Nachweis aufbewahrt werden muss. Dann bleibt der Widerruf der Einwilligung der Weg, der jede weitere Nutzung beendet.",
        ],
        actions: [
          { label: "Teilnahme sperren", where: "Not-Aus", path: "/not-aus" },
          { label: "Endgültig löschen", where: "Teilnehmer", path: "/teilnehmer" },
        ],
      },
      {
        id: "suche-protokolliert",
        question: "Warum steht jede Suche im Protokoll?",
        answer: [
          "Wer einen Teilnehmer aufruft, greift auf personenbezogene Daten zu. Jede Suche, die einen Teilnehmer findet, steht deshalb mit Ihrer Kennung im Protokoll, auch wenn danach nichts geändert wird.",
          "Die E-Mail-Adresse selbst wird dabei nicht gespeichert und erscheint in keiner Antwort.",
        ],
        actions: [{ label: "Suchen", where: "Teilnehmer", path: "/teilnehmer" }],
      },
      {
        id: "zugriffe-plattformbetrieb",
        question: "Was steht unter »Zugriffe des Plattformbetriebs«?",
        answer: [
          "Jede Anmeldung eines Supportkontos des Plattformbetriebs in diesem Backoffice und jede Änderung, die die Plattformverwaltung an Ihrem Bestand vornimmt, mit Zeitpunkt, Vorgang und Begründung. Sie sehen nur die Einträge Ihres eigenen Bestands.",
        ],
        actions: [{ label: "Zugriffe des Plattformbetriebs", where: "Protokoll", path: "/protokoll" }],
      },
    ],
  },
  {
    id: "gewinne",
    title: "Gewinne",
    entries: [
      {
        id: "zuteilung",
        question: "Wann erscheint eine Zuteilung?",
        answer: [
          "Die Ziehung läuft automatisch. Sobald sie einen Gewinner findet, steht die Zuteilung in der Liste der laufenden Aktion.",
        ],
      },
      {
        id: "anschrift",
        question: "Warum stehen keine Anschriften in der Liste?",
        answer: [
          "Anschriften werden einzeln abgerufen, und jeder Abruf wird protokolliert. Die Liste zeigt nur, ob eine Anschrift vorliegt.",
        ],
        actions: [{ label: "Anschrift anzeigen", where: "Gewinne", path: "/gewinne" }],
      },
      {
        id: "vermerk",
        question: "Was gehört in den Vermerk zur Sendung?",
        answer: [
          "Versandart oder Sendungsnummer. Er wird mit »Versendet« gespeichert.",
          "Keine Namen und keine Anschriften: Der Vermerk steht im Protokoll und wird nicht nach der kurzen Frist gelöscht, die für die Anschrift gilt.",
        ],
        actions: [{ label: "Versendet", where: "Gewinne", path: "/gewinne" }],
      },
    ],
  },
];
